/**
 * Game SFX: Web Audio API when possible, **HTMLAudioElement fallback** when buffers
 * are not ready yet or AudioContext is still `suspended` (common on TV / strict autoplay).
 */
export class SoundManager {
  private readonly actx: AudioContext;
  private readonly buffers = new Map<string, AudioBuffer>();
  private unlockIntervalId: ReturnType<typeof setInterval> | null = null;
  private readonly timeoutIds = new Set<ReturnType<typeof setTimeout>>();
  private readonly eventCleanups: Array<() => void> = [];

  /** Строковые ключи: Int64 / большие id с нативной стороны. */
  private readonly fusedBombIds = new Set<string>();
  private readonly hitFruitIds = new Set<string>();
  private prevFruitIds = new Set<string>();
  private lastFlyOutMs = 0;
  /** mp3 качаем только после preload() (старт калибровки Fruit Ninja), не на главном экране. */
  private mediaRequested = false;
  private static readonly FLY_OUT_COOLDOWN_MS = 1500;

  private static readonly SRCS = [
    '/media/cut_1.mp3',
    '/media/cut_2.mp3',
    '/media/cut_3.mp3',
    '/media/fly_out_1.mp3',
    '/media/fly_out_2.mp3',
    '/media/bomb_blast.mp3',
    '/media/bomb_fuse.mp3',
  ] as const;

  constructor() {
    this.actx = new AudioContext();

    const tryResume = (): void => {
      void this.actx.resume().catch(() => {/* ignore */});
    };

    const onGesture = (): void => {
      tryResume();
      if (this.mediaRequested) {
        for (const src of SoundManager.SRCS) {
          void this.warmHtmlAudio(src);
        }
      }
      if (this.actx.state === 'running' && this.unlockIntervalId !== null) {
        clearInterval(this.unlockIntervalId);
        this.unlockIntervalId = null;
      }
    };
    for (const ev of ['click', 'pointerdown', 'touchstart', 'keydown'] as const) {
      document.addEventListener(ev, onGesture, { capture: true, passive: true });
      this.eventCleanups.push(() =>
        document.removeEventListener(ev, onGesture, { capture: true }),
      );
    }
    window.addEventListener('focus', tryResume);
    this.eventCleanups.push(() => window.removeEventListener('focus', tryResume));
    const onVisibilityChange = (): void => {
      if (document.visibilityState === 'visible') tryResume();
    };
    document.addEventListener('visibilitychange', onVisibilityChange);
    this.eventCleanups.push(() =>
      document.removeEventListener('visibilitychange', onVisibilityChange),
    );

    this.scheduleAggressiveUnlock(tryResume);
  }

  preload(): void {
    if (this.mediaRequested) return;
    this.mediaRequested = true;
    for (const src of SoundManager.SRCS) {
      void this.preloadWebAudio(src);
      void this.warmHtmlAudio(src);
    }
  }

  retryAudioUnlock(): void {
    const tryResume = (): void => {
      void this.actx.resume().catch(() => {/* ignore */});
    };
    for (const ms of [0, 40, 120, 400]) {
      this.setManagedTimeout(tryResume, ms);
    }
    this.setManagedTimeout(() => this.trySilentHtml5Audio(tryResume), 80);
    this.trySyntheticOffscreenButton(tryResume);
    if (!this.mediaRequested) return;
    for (const src of SoundManager.SRCS) {
      void this.warmHtmlAudio(src);
    }
  }

  private scheduleAggressiveUnlock(tryResume: () => void): void {
    for (const ms of [0, 16, 50, 100, 200, 400, 800, 1500, 3000, 5000]) {
      this.setManagedTimeout(() => tryResume(), ms);
    }

    this.setManagedTimeout(() => {
      this.trySilentHtml5Audio(tryResume);
      this.trySyntheticOffscreenButton(tryResume);
    }, 120);
    this.setManagedTimeout(() => this.trySilentHtml5Audio(tryResume), 900);

    window.addEventListener(
      'load',
      () => {
        tryResume();
        this.trySilentHtml5Audio(tryResume);
      },
      { once: true },
    );

    let ticks = 0;
    if (this.unlockIntervalId !== null) clearInterval(this.unlockIntervalId);
    this.unlockIntervalId = setInterval(() => {
      tryResume();
      ticks += 1;
      if (this.actx.state === 'running' || ticks > 50) {
        clearInterval(this.unlockIntervalId!);
        this.unlockIntervalId = null;
      }
    }, 350);
  }

  private trySilentHtml5Audio(after: () => void): void {
    try {
      const a = new Audio(
        'data:audio/wav;base64,UklGRigAAABXQVZFZm10IBIAAAABAAEARKwAAIhYAQACABAAAABkYXRhAgAAAAEA',
      );
      a.volume = 0.0001;
      void a
        .play()
        .then(() => {
          a.pause();
          after();
        })
        .catch(() => {/* blocked */});
    } catch {
      /* ignore */
    }
  }

  private trySyntheticOffscreenButton(tryResume: () => void): void {
    try {
      const b = document.createElement('button');
      b.type = 'button';
      b.setAttribute('aria-hidden', 'true');
      b.tabIndex = -1;
      b.style.cssText = 'position:fixed;left:-9999px;top:0;width:1px;height:1px;opacity:0;pointer-events:none';
      b.addEventListener(
        'click',
        () => {
          tryResume();
        },
        { once: true },
      );
      document.body.appendChild(b);
      b.click();
      this.setManagedTimeout(() => b.remove(), 50);
    } catch {
      /* ignore */
    }
  }

  private async preloadWebAudio(src: string): Promise<void> {
    try {
      const res = await fetch(src);
      const raw = await res.arrayBuffer();
      const buf = await this.actx.decodeAudioData(raw.slice(0));
      this.buffers.set(src, buf);
    } catch {
      /* decode error — HTML5 fallback will still work */
    }
  }

  /** Prime browser cache / decode pipeline for HTMLAudio fallback. */
  private warmHtmlAudio(src: string): Promise<void> {
    return new Promise((resolve) => {
      try {
        const a = new Audio(src);
        a.preload = 'auto';
        a.load();
        a.addEventListener('canplaythrough', () => resolve(), { once: true });
        a.addEventListener('error', () => resolve(), { once: true });
        setTimeout(resolve, 4000);
      } catch {
        resolve();
      }
    });
  }

  /**
   * Web Audio if context is running and buffer ready; otherwise **HTMLAudioElement**
   * (still works when buffers are loading or AudioContext is suspended).
   */
  private play(src: string, volume = 1): void {
    void this.actx.resume().catch(() => {/* ignore */});

    const buf = this.buffers.get(src);
    const canWebAudio = buf !== undefined && this.actx.state === 'running';

    if (canWebAudio) {
      try {
        const source = this.actx.createBufferSource();
        source.buffer = buf;
        const gain = this.actx.createGain();
        gain.gain.value = volume;
        source.connect(gain);
        gain.connect(this.actx.destination);
        source.start(0);
        return;
      } catch {
        /* fall through to HTML5 */
      }
    }

    this.playHtml5(src, volume);
  }

  private playHtml5(src: string, volume: number): void {
    try {
      const a = new Audio(src);
      a.volume = volume;
      void a.play().catch(() => {/* autoplay blocked or decode error */});
    } catch {
      /* ignore */
    }
  }

  private rand(arr: string[]): string {
    return arr[Math.floor(Math.random() * arr.length)]!;
  }

  playFruitCut(): void {
    this.play(this.rand(['/media/cut_1.mp3', '/media/cut_2.mp3', '/media/cut_3.mp3']));
  }

  playBombBlast(): void {
    this.play('/media/bomb_blast.mp3');
  }

  /** Fuse once per bomb id; caller should only pass on-screen bombs (see RemoteFruitDisplayEngine). */
  tickBombFuse(activeBombIds: Iterable<number | string>): void {
    for (const raw of activeBombIds) {
      const id = typeof raw === 'string' ? raw : String(raw);
      if (!this.fusedBombIds.has(id)) {
        this.fusedBombIds.add(id);
        this.play('/media/bomb_fuse.mp3', 0.8);
      }
    }
  }

  tickFlyOut(currentFruits: ReadonlyArray<{ id?: number | string; is_hit: boolean }>): void {
    const currentIds = new Set<string>();
    for (const f of currentFruits) {
      if (f.id !== undefined && f.id !== null && `${f.id}` !== '') {
        const id = typeof f.id === 'string' ? f.id : String(f.id);
        currentIds.add(id);
        if (f.is_hit) this.hitFruitIds.add(id);
      }
    }

    const now = Date.now();
    for (const prevId of this.prevFruitIds) {
      if (!currentIds.has(prevId) && !this.hitFruitIds.has(prevId)) {
        if (now - this.lastFlyOutMs >= SoundManager.FLY_OUT_COOLDOWN_MS) {
          this.play(this.rand(['/media/fly_out_1.mp3', '/media/fly_out_2.mp3']), 0.6);
          this.lastFlyOutMs = now;
        }
        this.fusedBombIds.delete(prevId);
      }
      if (!currentIds.has(prevId)) {
        this.hitFruitIds.delete(prevId);
      }
    }
    this.prevFruitIds = currentIds;
  }

  /**
   * Вызывать по клику на кнопку «Включить звук» — надёжный user gesture для браузера.
   */
  unlockFromButton(btn?: HTMLElement): void {
    void this.actx
      .resume()
      .then(() => {
        if (btn) btn.classList.add('hidden');
      })
      .catch(() => {/* ignore */});
    this.retryAudioUnlock();
  }

  reset(): void {
    this.fusedBombIds.clear();
    this.hitFruitIds.clear();
    this.prevFruitIds.clear();
    this.lastFlyOutMs = 0;
  }

  dispose(): void {
    if (this.unlockIntervalId !== null) {
      clearInterval(this.unlockIntervalId);
      this.unlockIntervalId = null;
    }
    this.timeoutIds.forEach((id) => clearTimeout(id));
    this.timeoutIds.clear();
    this.eventCleanups.forEach((cleanup) => cleanup());
    this.eventCleanups.length = 0;
    void this.actx.close().catch(() => {
      /* ignore */
    });
  }

  private setManagedTimeout(cb: () => void, ms: number): void {
    const id = setTimeout(() => {
      this.timeoutIds.delete(id);
      cb();
    }, ms);
    this.timeoutIds.add(id);
  }
}
